import React, { useState, useEffect } from 'react'
import { Button, Card, CardContent, Typography, Box, Alert, TextField } from '@mui/material'
import { useAuth } from 'react-oidc-context'

const WebSocketDiagnostics: React.FC = () => {
  const auth = useAuth()
  const [diagnostics, setDiagnostics] = useState<string[]>([])
  const [testSocket, setTestSocket] = useState<WebSocket | null>(null)
  const [deviceId, setDeviceId] = useState('')
  const [tenantId, setTenantId] = useState(String(auth.user?.profile?.customer ?? ''))
  const [username, setUsername] = useState(String(auth.user?.profile?.name ?? ''))

  // Update values when auth changes
  useEffect(() => {
    if (auth.user?.profile?.customer !== undefined && auth.user.profile.customer !== '') {
      setTenantId(String(auth.user.profile.customer))
    }
    if (auth.user?.profile?.name !== undefined && auth.user.profile.name !== '') {
      setUsername(String(auth.user.profile.name))
    }
  }, [auth.user])

  // No log appending, diagnostics will remain empty
  const testConnection = (): void => {
    setDiagnostics([])

    const urls = [
      `${(window._env_?.REACT_APP_WEBSOCKET_HOST as string) ?? ''}/udp/tiq/ws/dashboard`
    ]

    const testUrl = async (url: string): Promise<boolean> => {
      return await new Promise((resolve) => {
        const socket = new WebSocket(url)

        const timeout = setTimeout(() => {
          socket.close()
          resolve(false)
        }, 5000)

        socket.onopen = (): void => {
          clearTimeout(timeout)
          socket.close()
          resolve(true)
        }

        socket.onerror = (error): void => {
          clearTimeout(timeout)
          console.error(error)
        }

        socket.onclose = (_event): void => {
          // onclose intentionally left empty
        }
      })
    }

    // Test each URL sequentially
    const testAllUrls = async (): Promise<void> => {
      for (const url of urls) {
        await testUrl(url)
      }
    }

    void testAllUrls()
  }

  const testWithAuth = (): void => {
    setDiagnostics([])

    const url = `${(window._env_?.REACT_APP_WEBSOCKET_HOST as string) ?? ''}/udp/tiq/ws/dashboard?tenantId=${tenantId}&username=${username}`

    const socket = new WebSocket(url)

    const timeout = setTimeout(() => {
      socket.close()
    }, 10000)

    socket.onopen = (): void => {
      clearTimeout(timeout)

      // Send a test message
      const testMessage = {
        type: 'DASHBOARD_MODBUS_DATA_SUBSCRIBE',
        data: {
          deviceId,
          tenantId,
          username
        },
        correlationId: 'test-123'
      }

      socket.send(JSON.stringify(testMessage))
    }

    socket.onerror = (error): void => {
      clearTimeout(timeout)
      console.error(error)
    }

    socket.onclose = (_event): void => {
      clearTimeout(timeout)
      // onclose intentionally left empty
    }

    socket.onmessage = (_event): void => {
      // onmessage intentionally left empty
    }

    setTestSocket(socket)
  }

  const closeConnection = (): void => {
    if (testSocket !== null) {
      testSocket.close()
      setTestSocket(null)
    }
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        WebSocket Connection Diagnostics
      </Typography>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Authentication Status
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
            <Alert severity={auth.isAuthenticated ? 'success' : 'warning'} sx={{ padding: '0 8px' }}>
              {auth.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </Alert>
            {auth.isAuthenticated && (
              <Typography variant="body2" color="text.secondary">
                User: {String(auth.user?.profile?.name ?? '')} | Tenant: {String(auth.user?.profile?.customer ?? '')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test Configuration
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Tenant ID"
                value={tenantId}
                onChange={(e) => { setTenantId(e.target.value) }}
                size="small"
              />
              <TextField
                label="Username"
                value={username}
                onChange={(e) => { setUsername(e.target.value) }}
                size="small"
              />
            </Box>

            <TextField
              label="Device ID"
              value={deviceId}
              onChange={(e) => { setDeviceId(e.target.value) }}
              size="small"
              fullWidth
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ marginBottom: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connection Tests
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, marginBottom: 2 }}>
            <Button variant="contained" onClick={testConnection}>
              Test Common URLs
            </Button>
            <Button variant="contained" onClick={testWithAuth}>
              Test with Auth
            </Button>
            <Button variant="outlined" onClick={closeConnection}>
              Close Connection
            </Button>
          </Box>

          <Alert severity="info" sx={{ marginBottom: 2 }}>
            <Typography variant="body2">
              <strong>Common Issues:</strong><br/>
              • Backend server not running<br/>
              • Wrong port or path<br/>
              • CORS issues<br/>
              • Authentication required
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Diagnostic Log
          </Typography>

          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              padding: 1,
              backgroundColor: '#f5f5f5',
              fontFamily: 'monospace'
            }}
          >
            {diagnostics.length === 0
              ? (
                  <Typography variant="body2" color="text.secondary">
                    Click &quot;Test Common URLs&quot; or &quot;Test with Auth&quot; to start diagnostics
                  </Typography>
                )
              : (
                  diagnostics.map((log, index) => (
                    <div key={index} style={{ marginBottom: 4 }}>
                      {log}
                    </div>
                  ))
                )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default WebSocketDiagnostics
